import React, { useMemo } from "react";
import { motion } from "framer-motion";
import { Volume2, VolumeX } from "lucide-react";
import { useEngineSound, playShimmer } from "./sound";

export const GOLD = "#F5C518";
export const CRIMSON = "#C41E24";

// Mission Control chassis: smoked glass over carbon, deep red under-glow.
const GLASS = {
  background: "linear-gradient(160deg, rgba(34,34,38,0.72) 0%, rgba(14,14,16,0.82) 55%, rgba(26,10,11,0.85) 100%)",
  backdropFilter: "blur(18px)",
  WebkitBackdropFilter: "blur(18px)",
  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.10), 0 0 0 1px rgba(196,30,36,0.35), 0 0 46px rgba(196,30,36,0.28), 0 30px 80px rgba(0,0,0,0.75)",
};
const CARBON = {
  backgroundImage: "linear-gradient(27deg,#141414 5px,transparent 5px),linear-gradient(207deg,#141414 5px,transparent 5px),linear-gradient(27deg,#191919 5px,transparent 5px),linear-gradient(207deg,#191919 5px,transparent 5px)",
  backgroundSize: "10px 18px", backgroundPosition: "0 0,0 0,5px 9px,5px 9px", backgroundColor: "#0a0a0a",
};

export const StatusLight = ({ delay = 0, color = GOLD, size = 7 }) => (
  <motion.span className="inline-block rounded-full flex-shrink-0" style={{ width: size, height: size, background: color, boxShadow: `0 0 8px ${color}, 0 0 2px #fff inset` }}
    animate={{ opacity: [1, 0.2, 1] }} transition={{ duration: 1.2, repeat: Infinity, delay }} />
);

const items = (engine) => [
  "MISSION CONTROL", "SYS.LINK 0x3F", "FRAME_SYNC OK",
  ...(engine ? [`BAL ${engine.balance}.0`, `EXE ${engine.execution}.0`, `PACE ${engine.pace}.0`] : ["SCAN", "CALIBRATING"]),
  "POSE_RIG 13pt", "LAT 0.02s", "CONF 98.2%", "PX_MAP v2", "SLOWMO x0.33", "GRID LOCK", "NEXT O ENGINE",
];

// Each telemetry token flickers on its own rhythm.
const Tok = ({ t, i, className }) => (
  <motion.span className={className} animate={{ opacity: [0.35, 1, 0.5, 0.95, 0.3] }}
    transition={{ duration: 1.4 + (i % 5) * 0.37, repeat: Infinity, delay: (i % 7) * 0.23, ease: "linear" }}>{t}</motion.span>
);

export const TelemetryTicker = ({ engine }) => {
  const s = items(engine);
  return (
    <div className="relative overflow-hidden h-5 bg-black/60 border-t border-[#C41E24]/25">
      <motion.div className="absolute whitespace-nowrap font-mono text-[9px] tracking-[0.22em] text-[#F5C518] leading-5"
        animate={{ x: ["0%", "-50%"] }} transition={{ duration: 24, repeat: Infinity, ease: "linear" }}>
        {[...s, ...s].map((t, i) => <Tok key={i} t={t} i={i} className="mx-5 inline-block" />)}
      </motion.div>
    </div>
  );
};

const VerticalTelemetry = ({ engine, dir = 1 }) => {
  const s = items(engine);
  return (
    <div className="relative overflow-hidden w-4 sm:w-5 flex-shrink-0 bg-black/50 border-x border-[#C41E24]/20">
      <div className="absolute inset-y-0 left-0 w-px" style={{ background: `linear-gradient(180deg, transparent, ${CRIMSON}, transparent)`, boxShadow: `0 0 8px ${CRIMSON}` }} />
      <motion.div className="absolute left-1/2 -translate-x-1/2 top-0 font-mono text-[7px] tracking-[0.3em] text-[#F5C518]/70 whitespace-nowrap" style={{ writingMode: "vertical-rl" }}
        animate={{ y: dir > 0 ? ["0%", "-50%"] : ["-50%", "0%"] }} transition={{ duration: 30, repeat: Infinity, ease: "linear" }}>
        {[...s, ...s].map((t, i) => <Tok key={i} t={t} i={i} className="py-3 inline-block" />)}
      </motion.div>
    </div>
  );
};

// Dashboard arc gauge (sports-car cluster style).
const Gauge = ({ label, value, delay = 0 }) => {
  const id = useMemo(() => `g${Math.random().toString(36).slice(2, 8)}`, []);
  const r = 26, c = Math.PI * r, pct = Math.max(0, Math.min(1, (value ?? 0) / 10));
  return (
    <div data-testid={`gauge-${label.toLowerCase()}`} className="flex flex-col items-center px-2">
      <svg width="68" height="40" viewBox="0 0 68 40">
        <defs><linearGradient id={id} x1="0" x2="1"><stop offset="0%" stopColor={CRIMSON} /><stop offset="100%" stopColor={GOLD} /></linearGradient></defs>
        <path d="M8 36 A26 26 0 0 1 60 36" fill="none" stroke="rgba(255,255,255,0.10)" strokeWidth="5" strokeLinecap="round" />
        <motion.path d="M8 36 A26 26 0 0 1 60 36" fill="none" stroke={`url(#${id})`} strokeWidth="5" strokeLinecap="round"
          style={{ filter: `drop-shadow(0 0 5px ${GOLD}aa)` }} strokeDasharray={c} initial={{ strokeDashoffset: c }} animate={{ strokeDashoffset: c * (1 - pct) }} transition={{ duration: 1.1, delay, ease: "easeOut" }} />
        <text x="34" y="35" textAnchor="middle" fill={GOLD} fontFamily="Roboto Mono, monospace" fontSize="13" fontWeight="700">{value ?? "--"}</text>
      </svg>
      <span className="font-mono text-[7px] tracking-[0.25em] text-white/50">{label}</span>
    </div>
  );
};

const Rivet = ({ className }) => (
  <span className={`absolute w-1.5 h-1.5 rounded-full ${className}`} style={{ background: "radial-gradient(circle at 35% 35%, #5a5a5a, #0a0a0a)", boxShadow: "0 0 0 1px rgba(0,0,0,0.7)" }} />
);

export const SoundToggle = ({ large = false }) => {
  const [on, toggle] = useEngineSound();
  return (
    <button type="button" data-testid="engine-sound-toggle" aria-label={on ? "Mute engine sound" : "Enable engine sound"}
      onClick={() => { toggle(); if (!on) setTimeout(playShimmer, 30); }}
      className={`flex items-center gap-1.5 rounded-full border transition-colors ${large ? "px-3 py-1.5 border-[#F5C518] bg-[#F5C518] text-black hover:bg-[#ffd94a]" : "px-2 py-0.5 border-[#F5C518]/60 bg-black/60 text-[#F5C518] hover:bg-[#F5C518]/15"}`}
      style={{ boxShadow: `0 0 ${large ? 18 : 10}px ${GOLD}66` }}>
      {on ? <Volume2 size={large ? 14 : 11} /> : <VolumeX size={large ? 14 : 11} />}
      <span className={`font-mono tracking-[0.22em] uppercase ${large ? "text-[9px] font-bold" : "text-[7px]"}`}>{on ? "Audio On" : "Muted"}</span>
    </button>
  );
};

// The Mission Control Analysis Station: glass chassis, SYSTEM_ACTIVE light, flickering telemetry edges, gauge cluster.
export const AnalysisStation = ({ engine, children, mode = "Analysis Active", compact = false, testId }) => (
  <div data-testid={testId} className={`relative rounded-2xl overflow-hidden border border-white/10 ${compact ? "p-1" : "p-1.5 sm:p-2"}`} style={GLASS}>
    <div className="absolute inset-0 pointer-events-none opacity-40" style={CARBON} />
    <div className="absolute inset-x-0 top-0 h-px pointer-events-none" style={{ background: `linear-gradient(90deg, transparent, ${CRIMSON}, ${GOLD}, ${CRIMSON}, transparent)`, boxShadow: `0 0 12px ${CRIMSON}` }} />
    <Rivet className="top-1.5 left-1.5" /><Rivet className="top-1.5 right-1.5" /><Rivet className="bottom-1.5 left-1.5" /><Rivet className="bottom-1.5 right-1.5" />
    <div className={`relative flex items-center justify-between px-2 ${compact ? "py-1" : "py-1.5"}`}>
      <div className="flex items-center gap-2 min-w-0">
        <StatusLight />
        <span className="font-mono text-[8px] sm:text-[9px] tracking-[0.3em] uppercase text-[#F5C518]">System_Active</span>
        {!compact && <span className="hidden sm:inline font-mono text-[8px] tracking-[0.25em] uppercase text-white/40 truncate">· Mission Control</span>}
      </div>
      <div className="flex items-center gap-2">
        <motion.span key={mode} initial={{ opacity: 0, x: 6 }} animate={{ opacity: 1, x: 0 }} className="font-mono text-[8px] tracking-[0.25em] uppercase whitespace-nowrap" style={{ color: mode.includes("Lock") ? CRIMSON : "rgba(255,255,255,0.6)", textShadow: mode.includes("Lock") ? `0 0 8px ${CRIMSON}` : "none" }}>◢ {mode}</motion.span>
        <SoundToggle />
      </div>
    </div>
    <div className="relative h-px mx-2 mb-1" style={{ background: `linear-gradient(90deg, ${CRIMSON}aa, transparent 60%)` }} />
    <div className="relative flex">
      {!compact && <VerticalTelemetry engine={engine} />}
      <div className="relative flex-1 min-w-0 bg-black overflow-hidden">
        {children}
        {["top-1 left-1 border-t-2 border-l-2", "top-1 right-1 border-t-2 border-r-2", "bottom-1 left-1 border-b-2 border-l-2", "bottom-1 right-1 border-b-2 border-r-2"].map((c) => (
          <div key={c} className={`absolute w-5 h-5 border-[#F5C518]/70 pointer-events-none z-20 ${c}`} />
        ))}
      </div>
      {!compact && <VerticalTelemetry engine={engine} dir={-1} />}
    </div>
    {!compact && (
      <div className="relative flex items-center justify-between mt-1 px-1 bg-black/40 rounded-md">
        <div className="flex items-center">
          <Gauge label="BAL" value={engine?.balance} /><Gauge label="EXE" value={engine?.execution} delay={0.15} /><Gauge label="PACE" value={engine?.pace} delay={0.3} />
        </div>
        <div className="hidden sm:flex flex-col items-end pr-2 font-mono text-[7px] tracking-[0.25em] text-white/45 leading-4">
          <Tok t="RIG · 13 JOINTS" i={1} /><Tok t="GLASS_LINK · STABLE" i={4} /><Tok t={`TELEMETRY · ${engine ? "LIVE" : "CAL"}`} i={2} className="text-[#C41E24]" />
        </div>
      </div>
    )}
    {!compact && <TelemetryTicker engine={engine} />}
  </div>
);
